import { useEffect, useRef, useState, useMemo } from 'react';
import { setupCanvas } from '@/utils/canvasHelpers';

interface RegionHull {
  region_id: string;
  region_label: string;
  region_score: number;
  hulls: Record<string, number[][]>; // frame_index -> [[x, y], [x, y], ...]
}

interface BodyRegionOverlayProps {
  videoElement: HTMLVideoElement | null;
  regionHulls: RegionHull[];
  currentFrame: number;
  totalFrames?: number;
  hullFrameCount?: number;
  width: number;
  height: number;
  className?: string;
  showOverlay?: boolean;
  overlayOpacity?: number;
  showHulls?: boolean;
  showLabels?: boolean;
  showScore?: boolean;
  mirrored?: boolean;
}

/**
 * BodyRegionOverlay renders colored overlays on body regions based on their scores.
 * - GREEN: Score > 80 (good form)
 * - YELLOW: Score 60-80 (moderate)
 * - RED: Score < 60 (needs improvement)
 */
function BodyRegionOverlay({
  videoElement,
  regionHulls,
  currentFrame,
  totalFrames,
  hullFrameCount,
  width,
  height,
  className,
  showOverlay = true,
  overlayOpacity = 0.5,
  showHulls = true,
  showLabels = true,
  showScore = true,
  mirrored = false,
}: BodyRegionOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Use static border width instead of pulsing animation to prevent constant re-renders
  const borderWidth = 4;

  // Memoize rounded dimensions
  const canvasWidth = useMemo(() => Math.round(width), [width]);
  const canvasHeight = useMemo(() => Math.round(height), [height]);

  // Track video metadata loading state to trigger re-render
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    if (!videoElement) return;

    const handleLoadedMetadata = () => {
      setVideoLoaded(true);
    };

    // Check if already loaded
    if (videoElement.readyState >= 1) {
      setVideoLoaded(true);
    }

    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoElement]);

  // Get color based on score (0-100)
  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#10b981'; // green-500
    if (score >= 60) return '#f59e0b'; // amber-500
    return '#ef4444'; // red-500
  };

  // Draw body region overlays
  useEffect(() => {
    if (!showOverlay) return;
    if (!videoElement) return;

    const ctx = setupCanvas(canvasRef, canvasWidth, canvasHeight);
    if (!ctx) return;

    try {
      // Reset canvas state completely
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.shadowBlur = 0;
      ctx.setLineDash([]);

    // Get video dimensions
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) {
      console.warn('[BodyRegionOverlay] Video dimensions are 0x0');
      return;
    }

    // Calculate the actual rendered video area within the canvas
    // This accounts for letterboxing when video aspect ratio differs from container
    const videoAspect = videoWidth / videoHeight;
    const canvasAspect = canvasWidth / canvasHeight;

    let renderWidth: number;
    let renderHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (videoAspect > canvasAspect) {
      // Video is wider than canvas - letterbox top/bottom
      renderWidth = canvasWidth;
      renderHeight = canvasWidth / videoAspect;
      offsetX = 0;
      offsetY = (canvasHeight - renderHeight) / 2;
    } else {
      // Video is taller than canvas - letterbox left/right
      renderHeight = canvasHeight;
      renderWidth = canvasHeight * videoAspect;
      offsetX = (canvasWidth - renderWidth) / 2;
      offsetY = 0;
    }

    // Scale factors map from video coordinates to rendered area
    const scaleX = renderWidth / videoWidth;
    const scaleY = renderHeight / videoHeight;

    // Draw each region hull
    regionHulls.forEach((regionHull) => {
      // Map frame index when hulls are resampled to a different frame count
      // This handles the case where athlete video has more frames than template
      let mappedFrame = currentFrame;
      if (totalFrames && hullFrameCount && totalFrames > 1 && hullFrameCount > 1 && totalFrames !== hullFrameCount) {
        mappedFrame = Math.floor(currentFrame * (hullFrameCount - 1) / (totalFrames - 1));
        mappedFrame = Math.min(mappedFrame, hullFrameCount - 1); // Clamp to valid range
      }
      const frameKey = mappedFrame.toString();
      const rawHull = regionHull.hulls[frameKey];

      if (!rawHull || rawHull.length < 3) {
        return;
      }

      // Frontend defense: filter out degenerate points at or near (0, 0).
      // These are MediaPipe artifacts for undetected landmarks that were not
      // caught by the backend visibility filter (e.g., cached evaluations from
      // before the backend fix was deployed).
      const hull = rawHull.filter(
        (p) => !(p[0] <= 0.001 && p[1] <= 0.001) && p[0] >= 0 && p[1] >= 0 && p[0] <= 1.0 && p[1] <= 1.0
      );

      if (hull.length < 3) {
        return;
      }

      const color = getScoreColor(regionHull.region_score);

      // Detect coordinate type and issues
      const rawXs = hull.map(p => p[0]);
      const rawYs = hull.map(p => p[1]);
      const rawMaxX = Math.max(...rawXs);
      const rawMaxY = Math.max(...rawYs);

      // Check if coordinates are normalized (0-1) vs pixel-based
      const areNormalized = rawMaxX <= 1.0 && rawMaxY <= 1.0 && rawMaxX > 0 && rawMaxY > 0;

      // Hull coordinates come from un-mirrored pose data. When should_mirror=True,
      // the backend swaps landmark indices via MIRROR_LANDMARK_MAP, so the hulls
      // already render on the correct anatomical side. No X-flip needed here.
      const displayPoints: number[][] = hull;

      // Detect negative coordinates from old buggy mirroring code
      const hasNegativeXCoords = rawMaxX < 0;

      // FIX: Correct negative X coordinates from cached buggy mirroring data
      // Old bug did: x = -original_x (wrong negation)
      // To recover original position: original_x = -buggy_x (just negate it back)
      let xMultiplier = 1;
      if (hasNegativeXCoords) {
        xMultiplier = -1;  // Negate to recover original position
      }

      // Determine effective scaling
      // Normalized coords (0-1) should map to the rendered video area
      // Pixel coords (e.g., 0-1920) should use the scale factor
      let effectiveScaleX = scaleX;
      let effectiveScaleY = scaleY;
      const effectiveOffsetX = offsetX;
      const effectiveOffsetY = offsetY;

      if (areNormalized) {
        // For normalized coords: multiply by rendered area size and add offset
        effectiveScaleX = renderWidth;
        effectiveScaleY = renderHeight;
      }

      // Calculate bounding box for this hull using the effective scale (with X correction and offset)
      const xs = displayPoints.map((p) => (p[0] * xMultiplier) * effectiveScaleX + effectiveOffsetX);
      const ys = displayPoints.map((p) => p[1] * effectiveScaleY + effectiveOffsetY);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const hullWidth = maxX - minX;
      const hullHeight = maxY - minY;

      // Skip hulls that are too small to be meaningful
      if (hullWidth < 5 || hullHeight < 5) {
        return;
      }

      // Create a fresh path for each hull
      ctx.beginPath();

      // Draw the hull polygon using effective scale (with X correction and offset)
      displayPoints.forEach((point, idx) => {
        const x = (point[0] * xMultiplier) * effectiveScaleX + effectiveOffsetX;
        const y = point[1] * effectiveScaleY + effectiveOffsetY;

        if (idx === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.closePath();

      // Fill the hull with semi-transparent color
      if (showHulls) {
        ctx.save();
        ctx.globalAlpha = overlayOpacity;
        ctx.fillStyle = color;
        ctx.fill();
        ctx.restore();
      }

      // Stroke the hull border with glow effect
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = color;
      ctx.lineWidth = borderWidth;
      ctx.shadowBlur = 15;
      ctx.shadowColor = color;
      ctx.stroke();
      ctx.restore();

      // Draw region label with smart positioning (use effective scale with X correction and offset)
      if (showLabels) {
        // Apply X correction to hull for label positioning - use the already-calculated bounding box
        const labelPos = {
          x: maxX + 20, // Position to the right of the hull
          y: (minY + maxY) / 2 - 10,
          align: 'left' as const,
          centroid: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
        };

        // Adjust if label would go off canvas
        const labelWidth = 180;
        const labelHeight = 20;
        if (labelPos.x + labelWidth > canvasWidth - 10) {
          // Position to the left instead
          labelPos.x = minX - labelWidth - 20;
        }
        if (labelPos.x < 10) {
          labelPos.x = 10;
        }
        if (labelPos.y < 10) {
          labelPos.y = 10;
        }
        if (labelPos.y + labelHeight > canvasHeight - 10) {
          labelPos.y = canvasHeight - labelHeight - 10;
        }
        if (!labelPos) return;

        const label = showScore
          ? `${regionHull.region_label}: ${Math.round(regionHull.region_score)}%`
          : regionHull.region_label;
        ctx.font = 'bold 14px sans-serif';

        const metrics = ctx.measureText(label);
        const padding = 6;
        const labelW = metrics.width + padding * 2;
        const labelH = 20;

        // Draw leader line from centroid to label
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(labelPos.centroid.x, labelPos.centroid.y);
        ctx.lineTo(labelPos.x + labelW / 2, labelPos.y + labelH / 2);
        ctx.stroke();
        ctx.restore();

        // Draw label background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
        ctx.fillRect(labelPos.x, labelPos.y, labelW, labelH);

        // Draw label border
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(labelPos.x, labelPos.y, labelW, labelH);

        // Draw label text
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, labelPos.x + padding, labelPos.y + labelH / 2);
      }
    });
    } catch (error) {
      console.error('[BodyRegionOverlay] Canvas drawing error:', error);
    }
  }, [
    showOverlay,
    videoElement,
    videoLoaded, // Re-run when video metadata loads
    regionHulls,
    currentFrame,
    totalFrames,
    hullFrameCount,
    canvasWidth,
    canvasHeight,
    overlayOpacity,
    showHulls,
    showLabels,
    showScore,
    borderWidth,
    mirrored,
  ]);

  // Don't render if overlay is disabled
  if (!showOverlay) {
    return null;
  }

  // Don't render if canvas dimensions are invalid
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    console.warn('[BodyRegionOverlay] Invalid canvas dimensions, not rendering:', { canvasWidth, canvasHeight });
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      width={canvasWidth}
      height={canvasHeight}
      className={`absolute inset-0 pointer-events-none z-10 ${className || ''}`}
      style={{ width: '100%', height: '100%' }}
      data-testid="body-region-overlay-canvas"
    />
  );
}

export default BodyRegionOverlay;
